import { GoogleOutlined } from "@ant-design/icons";
import { animated, useSpring } from "@react-spring/web";
import {
	App,
	Button,
	type ButtonProps,
	Divider,
	Form,
	Input,
	Typography,
} from "antd";
import { useAuthUser } from "hooks/useAuth";
import { useReducedMotion } from "hooks/useReducedMotion";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "store/auth.store";
import { api } from "utils/axios";
import {
	getIdToken,
	signInWithGoogle,
	signInWithPassword,
} from "utils/firebase";
import { rules } from "utils/form";
import { getErrorMsg } from "utils/geterror";
import { clearInviteToken, readInviteToken } from "utils/inviteToken";
import { analytics, trackEvents } from "../../utils/analytics";
import { type AuthFormError, toAuthFormError } from "./authFormError";
import { AuthErrorAlert } from "./components/AuthErrorAlert";
import { AuthPage } from "./components/AuthPage";

// Type assertion for Helmet component
const HelmetComponent = Helmet as any;

const { Text } = Typography;

export function SignInPage() {
	const { notification } = App.useApp();
	const isAuth = useAuthStore((state) => !!state.uid);
	const { authUser } = useAuthUser();

	const [loading, setLoading] = useState(false);
	const [inviteProcessed, setInviteProcessed] = useState(false);
	const [authCompleted, setAuthCompleted] = useState(false);
	// One slot, so a second refusal replaces the first rather than stacking.
	// Cleared at the start of every attempt, which is also what clears it on a
	// submit that then succeeds.
	const [formError, setFormError] = useState<AuthFormError | null>(null);

	const [searchParams] = useSearchParams();
	const invitationTokenParam = readInviteToken(searchParams.get("token"));
	const emailParam = searchParams.get("email") || "";
	const returnUrl = searchParams.get("returnUrl") || "/";

	const reduced = useReducedMotion();
	const cardSpring = useSpring({
		from: { opacity: 0, transform: "translateY(12px)" },
		to: { opacity: 1, transform: "translateY(0px)" },
		delay: 200,
		immediate: reduced,
	});

	analytics.page();

	/**
	 * Every refusal on this page goes through here — all five of them. The toast
	 * stays for continuity; the banner is the part that is still on screen when
	 * the user looks back at the form.
	 */
	const reportFailure = (error: unknown) => {
		notification.error(getErrorMsg(error));
		setFormError(toAuthFormError(error));
	};

	// New useEffect to handle invitation when already authenticated
	useEffect(() => {
		const processInvite = async () => {
			if (isAuth && invitationTokenParam && !inviteProcessed) {
				setLoading(true);
				try {
					const token = await getIdToken(); // Wait for the token
					if (!token) throw new Error("No authentication token available");

					const body = {
						email: emailParam,
						token: token, // Use the awaited token
						invitationToken: invitationTokenParam,
					};

					await api.post("/auth/accept-invite", body);
					notification.success({
						message: "Invite accepted",
						description:
							"The invite has been accepted and you joined a new team.",
					});
				} catch (error) {
					reportFailure(error);
				} finally {
					setLoading(false);
					clearInviteToken();
					setInviteProcessed(true);
					setAuthCompleted(true); // Mark as ready for navigation
				}
			} else if (isAuth && !invitationTokenParam) {
				// User is already authenticated without an invitation
				setAuthCompleted(true);
			}
		};

		processInvite();
	}, [isAuth, invitationTokenParam, inviteProcessed, emailParam]);

	// Only redirect after authentication is fully completed and invite is processed if needed
	if (isAuth && authCompleted) {
		if (!invitationTokenParam || inviteProcessed) {
			return <Navigate to={returnUrl} />;
		}
		return null; // Show nothing while processing invite
	}

	const signIn = async (form: any) => {
		setLoading(true);
		setFormError(null);
		// This handler posts the invite itself. Firebase signs the user in (and
		// flips isAuth) before that post returns, so without this the effect
		// above posts the same invite a second time and shows its failure
		// ("invite not exists") beside the success toast.
		if (invitationTokenParam) setInviteProcessed(true);
		try {
			const { user } = await signInWithPassword(form.email, form.password);
			if (!user.email) throw new Error("Email is required");

			const body = {
				email: user.email,
				fullName: user.displayName || "",
				token: await user.getIdToken(),
				invitationToken: invitationTokenParam,
				googleSignIn: false,
			};
			if (invitationTokenParam) {
				await api
					.post("/auth/accept-invite", body)
					.then(() =>
						notification.success({
							message: "Invite accepted",
							description:
								"The invite has been accepted and you joined a new team.",
						}),
					)
					.catch(reportFailure)
					.finally(clearInviteToken);
			}

			// Ensure authUser completes and state is updated
			const result = await authUser(body);

			// Add analytics tracking for successful sign-in
			if (result) {
				trackEvents.user.signIn();
				setAuthCompleted(true); // Mark authentication as fully completed
			}
		} catch (error) {
			reportFailure(error);
			setLoading(false);
		}
	};

	const googleSignIn = async () => {
		setLoading(true);
		setFormError(null);
		// This handler posts the invite itself. Firebase signs the user in (and
		// flips isAuth) before that post returns, so without this the effect
		// above posts the same invite a second time and shows its failure
		// ("invite not exists") beside the success toast.
		if (invitationTokenParam) setInviteProcessed(true);
		try {
			const { user } = await signInWithGoogle();
			if (!user.email) throw new Error("Email is required");
			const body = {
				email: user.email,
				fullName: user.displayName,
				token: await user.getIdToken(),
				invitationToken: invitationTokenParam,
				googleSignIn: true,
			};

			if (invitationTokenParam) {
				await api
					.post("/auth/accept-invite", body)
					.then(() =>
						notification.success({
							message: "Invite accepted",
							description:
								"The invite has been accepted and you joined a new team.",
						}),
					)
					.catch(reportFailure)
					.finally(clearInviteToken);
			}

			// Ensure authUser completes and state is updated
			const result = await authUser(body);

			// Add analytics tracking for successful Google sign-in
			if (result) {
				trackEvents.user.signInGoogle();
				setAuthCompleted(true); // Mark authentication as fully completed
			}
		} catch (error) {
			reportFailure(error);
			setLoading(false);
		}
	};

	const btnProps: ButtonProps = {
		loading,
		block: true,
		type: "primary",
	};

	return (
		<AuthPage>
			<HelmetComponent>
				<title>Equility - Sign In</title>
			</HelmetComponent>
			<animated.div style={cardSpring} className="auth-card">
				<div className="auth-card__head">
					<h1 className="auth-card__title">Welcome back</h1>
					<p className="auth-card__subtitle">
						Sign in to your Equility account to continue.
					</p>
				</div>
				<AuthErrorAlert error={formError} />
				<Button
					block
					loading={loading}
					onClick={googleSignIn}
					icon={<GoogleOutlined />}
					className="auth-oauth-btn"
				>
					Sign in with Google
				</Button>
				<Divider>or continue with email</Divider>
				<Form
					onFinish={signIn}
					layout="vertical"
					initialValues={{ email: emailParam }}
					validateTrigger="onBlur"
					onFinishFailed={(errorInfo) => {
						const f = errorInfo.errorFields?.[0]?.name?.[0];
						if (f)
							(
								document.querySelector(
									`#signin-${String(f)}`,
								) as HTMLInputElement | null
							)?.focus?.();
					}}
				>
					{/*
					 * `validateStatus`/`help` override the rules while they are set, and
					 * fall back to them the moment they are undefined — antd treats a
					 * null/undefined `help` as "use the field's own errors". So a
					 * refusal marks the field without disabling its rule.
					 */}
					<Form.Item
						label="Email"
						name="email"
						rules={rules.email}
						validateStatus={formError?.field === "email" ? "error" : undefined}
						help={
							formError?.field === "email" ? formError.description : undefined
						}
					>
						<Input
							id="signin-email"
							placeholder="you@example.com"
							autoComplete="email"
						/>
					</Form.Item>
					<Form.Item
						label="Password"
						name="password"
						rules={rules.passwordLogin}
						validateStatus={
							formError?.field === "password" ? "error" : undefined
						}
						help={
							formError?.field === "password"
								? formError.description
								: undefined
						}
					>
						<Input.Password
							id="signin-password"
							placeholder="Your password"
							autoComplete="current-password"
						/>
					</Form.Item>
					<Form.Item>
						<Button {...btnProps} htmlType="submit" className="auth-submit-btn">
							Sign In
						</Button>
					</Form.Item>
				</Form>
				<div className="auth-card__links">
					<Link to="/forgot">Forgot your password?</Link>
					<span>
						<Text type="secondary">New customer? </Text>
						<Link to="/signup">Start here</Link>
					</span>
				</div>
			</animated.div>
		</AuthPage>
	);
}
